import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { X } from "lucide-react";
import { cn } from "@edusogno/lib/utils";
import { ExerciseType } from "@/components/v3/exercises/ExerciseBadge";
import {
  useExerciseState,
  useExerciseAction,
} from "@/contexts/exercise/Context";
import dayjs, { Dayjs } from "dayjs";
import Timer from "@/components/v3/exercises/Timer";
import Button from "@edusogno/primitives/button.js";

type Props = {
  type?: ExerciseType | string;
  timer?: number;
  onClose?: () => void;
  isResult?: boolean;
};

interface CountdownHandle {
  start: () => void;
}

const ActionBarGrammar = forwardRef<CountdownHandle, Props>(
  ({ onClose, timer, isResult }: Props, ref) => {
    const { startSequence } = useExerciseAction();
    const exerciseStat = useExerciseState();
    const { isExerciseStarted } = exerciseStat;

    const startTimer = useRef<Dayjs>(dayjs());
    const countDownRef = useRef<CountdownHandle>(null);

    const [isTimerVisible, setIsTimerVisible] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    useImperativeHandle(ref, () => ({
      start() {
        if (countDownRef.current) {
          countDownRef.current.start();
        }
      },
    }));

    const handleStart = () => {
      // if (!timer) {
      //   console.error("The timer is not set or 0!", timer);
      //   return;
      // }

      const newExerciseIndex = exerciseStat?.exerciseResults?.length ?? 0;

      startSequence(Date.now(), (timer ?? 1) * 60, newExerciseIndex);
      setIsTimerVisible(true);
    };

    useEffect(() => {
      if (isTimerVisible && countDownRef.current) {
        countDownRef.current.start();
      }
    }, [isTimerVisible]);

    return (
      <div
        className={`${
          isResult ? "hidden lg:flex" : "flex"
        } flex-row items-center h-[50px] gap-4`}
      >
        {showConfirmation && (
          <>
            <p className="w-auto text-end text-white font-medium text-xs">
              If you exit now, the exercise will be moved to the 'standby'
              section. Confirm??
            </p>

            <div className="gap-1 flex flex-row">
              <Button
                onClick={() => setShowConfirmation(false)}
                size="sm"
                color="green"
                className="rounded-[10px] [--bg:#FE9595] min-w-[45px] w-[45px]  h-[30px]"
              >
                No
              </Button>

              <Button
                onClick={onClose}
                size="sm"
                color="green"
                className="rounded-[10px] min-w-[45px] w-[45px]  h-[30px]"
              >
                Yes
              </Button>
            </div>
          </>
        )}

        <div
          className={cn("flex flex-row gap-4 items-center", {
            hidden: showConfirmation,
          })}
        >
          {isTimerVisible && (
            <Timer
              run
              onStart={(s) => {
                startTimer.current = s as Dayjs;
              }}
              duration={0}
            />
            // <CountdownTimer ref={countDownRef} maxTime={timer ?? 10} />
          )}

          {!isResult && !isExerciseStarted && (
            <Button
              size="sm"
              onClick={handleStart}
              className="rounded-[10px] w-[100px] h-[30px]"
            >
              Start
            </Button>
          )}

          <Button
            color="white"
            variant="ghost"
            onClick={() => {
              if (!isExerciseStarted) {
                if (onClose) onClose();
              } else {
                setShowConfirmation(true);
              }
            }}
            className="w-fit"
          >
            <X />
          </Button>
        </div>
      </div>
    );
  },
);

export default ActionBarGrammar;
